import React from 'react';
import ReactSlider from 'react-slider';
import './Slider.scss';
import { ClassNameProp } from '../common-types';

/**
 * Props for a {@link Slider} component.
 * @author branislav.kapral
 */
export interface SliderProps extends ClassNameProp {
  /**
   * Aria-label for screen-readers to apply to the thumb.
   */
  ariaLabel?: string;
  /**
   * Aria-labelledby for screen-readers to apply to the thumb.
   */
  ariaLabelledby?: string;
  /**
   * Aria-valuetext for screen-readers
   */
  ariaValuetext?: string;
  /**
   * Determines the initial positions of the thumb.
   */
  defaultValue?: number;
  /**
   * If true the thumb can't be moved.
   */
  disabled?: boolean;
  /**
   * Inverts the slider.
   */
  invert?: boolean;
  /**
   * The maximum value of the slider.
   */
  max: number;
  /**
   * The minimum value of the slider.
   */
  min: number;
  /**
   * Callback called only after moving a thumb has ended.
   */
  onAfterChange?: (value: number) => void;
  /**
   * Callback called before starting to move a thumb.
   */
  onBeforeChange?: (value: number) => void;
  /**
   * Callback called on every value change.
   */
  onChange?: (value: number) => void;
  /**
   * Callback called when the the slider is clicked (thumb or tracks).
   */
  onSliderClick?: (value: number) => void;
  /**
   * Determines whether the slider moves horizontally (from left to right) or vertically (from bottom to top).
   */
  orientation?: 'horizontal' | 'vertical';
  /**
   * Value to be added or subtracted on each step the slider makes.
   */
  step?: number;
  /**
   * Like defaultValue but for controlled components.
   */
  value?: number; // Sets initial value and also sets component to controlled mode
}

/**
 * Slider component
 *
 * @param props the {@link SliderProps} properties
 * @return the created {@link JSX.Element}
 * @constructor
 * @author branislav.kapral
 */
export function Slider({
  ariaLabel,
  ariaLabelledby,
  ariaValuetext,
  className,
  defaultValue = 0,
  disabled,
  invert,
  max,
  min,
  onAfterChange,
  onBeforeChange,
  onChange,
  onSliderClick,
  orientation = 'horizontal',
  step,
  value
}: SliderProps): JSX.Element {
  // Used Library has unintuitive orientation for vertical orientation
  const isInvert = orientation === 'vertical' ? !invert : invert;
  return (
    <ReactSlider
      ariaLabel={ariaLabel}
      ariaLabelledby={ariaLabelledby}
      ariaValuetext={ariaValuetext}
      className={`ehs-slider ${orientation} ${className}`}
      orientation={orientation}
      disabled={disabled}
      defaultValue={defaultValue}
      invert={isInvert}
      max={max}
      min={min}
      onAfterChange={onAfterChange}
      onBeforeChange={onBeforeChange}
      onChange={onChange}
      onSliderClick={onSliderClick}
      step={step}
      thumbActiveClassName="ehs-slider-active-thumb"
      thumbClassName="ehs-slider-thumb"
      trackClassName={'ehs-slider-track ' + orientation}
      value={value}
    />
  );
}
